import React, { useState, useCallback, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useMe } from 'contexts/me'
import { UnitPermissionsProvider, useUnitPermissions } from 'contexts/unit-permissions'
import { unpackField } from 'utilities/person'
import Message from 'components/message'
import { InlineButton } from 'components/button'

const DocumentAccessWarnings = ({ access, setAccess, children: fallback = null }) => {
    const { formatMessage } = useIntl()

    const {
        me,

        isItMyOwnId,
        getListMe
    } = useMe()

    const {
        checkOrganizationUnitPermission,
        checkSpecificUnitPermission
    } = useUnitPermissions()

    const [sharedIndirectly, setSharedIndirectly] = useState(checkSharedIndirectly(access.value, me))
    const [ownedIndirectly, setOwnedIndirectly] = useState(false)
    const [checking, setChecking] = useState(false)

    const noShares = !access.value.filter(({ access }) => access.share).length
    const noOwners = !access.value.filter(({ access }) => access.owner).length

    const checkOwnedIndirectly = useCallback(async accessUnits => {
        if(!!accessUnits.find(({ unit }) => unit.type === 'organization')?.access?.owner && checkOrganizationUnitPermission('unit:documents:manage')) {
            return void setOwnedIndirectly(true)
        }

        if(checking) {
            return
        }

        setChecking(true)

        const groups = accessUnits
            .filter(({ unit }) => ['team', 'location'].includes(unit.type))
            .filter(({ access }) => access.owner)
            .map(({ unit }) => unit)

        const results = await Promise.all(groups.map(unit =>
            checkSpecificUnitPermission({ key: 'unit:documents:manage', unit })
        ))

        setOwnedIndirectly(results.some(Boolean))
        setChecking(false)
    }, [checking])

    useEffect(() => {
        !noShares && setSharedIndirectly(checkSharedIndirectly(access.value, me))
        !!noShares && setSharedIndirectly(false)
    }, [noShares, access.value])

    useEffect(() => {
        !noOwners && checkOwnedIndirectly(access.value)
        !!noOwners && setOwnedIndirectly(false)
    }, [noOwners, access.value])

    if(noOwners) {
        return (
            <Message
                type="warning"
                message={formatMessage({
                    id: 'document_access_owners_missing_warning',
                    defaultMessage: 'At least one unit needs to have <strong>See and edit<\/strong> access.'
                })} />
        )
    }

    if(checking) {
        return null
    }

    const myAccess = access.value.find(({ unit }) => isItMyOwnId(unit.id))?.access
    const ownedDirectly = !!myAccess?.owner
    const sharedDirectly = !!myAccess?.share

    if((sharedDirectly || sharedIndirectly) && !ownedDirectly && !ownedIndirectly) {
        return (
            <Message
                type="warning"
                message={formatMessage({
                    id: 'document_owner_change_warning',
                    defaultMessage: 'You will no longer be able to edit this document’s details.'
                })} />
        )
    }

    if(!sharedDirectly && !sharedIndirectly && !ownedDirectly && !ownedIndirectly) {
        return (
            <Message
                type="warning"
                textClassName="preserve-line-breaks"
                message={formatMessage({
                    id: 'document_lose_access_change_warning',
                    defaultMessage: 'You will no longer be able to access this document.'
                })}>
                <InlineButton
                    className="underline-reversed"
                    onClick={() => setAccess(access => ({
                        value: [
                            {
                                unit: getListMe(),
                                access: {
                                    share: true,
                                    owner: false
                                }
                            },
                            ...access.value
                        ],
                        changed: true
                    }))}>
                    <FormattedMessage
                        id="document_owner_change_keep_access_label"
                        defaultMessage="Keep read access" />
                </InlineButton>
            </Message>
        )
    }

    return fallback
}

const checkSharedIndirectly = (accessUnits, me) => {
    if(!!accessUnits.find(({ unit }) => unit.type === 'organization')?.access?.share) {
        return true
    }

    const accessGroupIds = accessUnits
        .filter(({ unit }) => ['team', 'location'].includes(unit.type))
        .filter(({ access }) => access.share)
        .map(({ unit }) => unit.id)

    const myGroupIds = [
        ...unpackField(me.teams),
        ...unpackField(me.locations)
    ].map(({ id }) => id)

    return accessGroupIds.some(id => myGroupIds.includes(id))
}

export default props => (
    <UnitPermissionsProvider>
        <DocumentAccessWarnings {...props} />
    </UnitPermissionsProvider>
)